import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

// ── Types ───────────────────────────────────────────────────────────────────

export type UsageSummary = {
  workflowRuns: number;
  workflowRunsByType: Record<string, number>;
  nodesExecuted: number;
  nodesByTier: Record<string, number>;
  browserActions: number;
  browserActionsByMode: Record<string, number>;
  browserActionsByCategory: { read: number; act: number };
};

export type UsageTimeseriesBucket = {
  bucket: string;
  workflowRuns: number;
  /** Distinct runs with ≥1 agent (l3) step in this bucket — the quota metric. */
  agenticRuns: number;
  nodesExecuted: number;
  browserActions: number;
};

// ── Helpers ─────────────────────────────────────────────────────────────────

export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

async function fetchUsage<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, `Failed to fetch ${path}`);
  return res.json();
}

// ── Hooks ───────────────────────────────────────────────────────────────────

export function useUsageSummary(start: string, end: string) {
  const qs = new URLSearchParams({ start, end }).toString();
  return useQuery({
    queryKey: ['usage-summary', qs],
    queryFn: () => fetchUsage<{ ok: boolean; data: UsageSummary }>(`/usage/summary?${qs}`),
    staleTime: 30_000,
  });
}

// ── Quota ──────────────────────────────────────────────────────────────────

export type UsageQuotaResponse = {
  quota: { monthStartDay: number; monthlyRunQuota: number | null };
  period: { start: string; end: string };
  usage: { agenticRuns: number };
};

export function useUsageQuota() {
  return useQuery({
    queryKey: ['usage-quota'],
    queryFn: async (): Promise<UsageQuotaResponse | null> => {
      const res = await fetch(`${API_URL}/usage/quota`, { credentials: 'include' });
      if (res.status === 404) return null;
      if (!res.ok) throw await toApiError(res, 'Failed to fetch usage quota');
      const json: { ok: boolean; data: UsageQuotaResponse } = await res.json();
      return json.data;
    },
    staleTime: 300_000,
    retry: false,
  });
}

// ── Timeseries ─────────────────────────────────────────────────────────────

export function useUsageTimeseries(start: string, end: string, bucketMinutes: number) {
  const qs = new URLSearchParams({
    start,
    end,
    bucketMinutes: String(bucketMinutes),
  }).toString();
  return useQuery({
    queryKey: ['usage-timeseries', qs],
    queryFn: () =>
      fetchUsage<{ ok: boolean; data: UsageTimeseriesBucket[] }>(`/usage/timeseries?${qs}`),
    staleTime: 30_000,
  });
}
